import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, IconButton, Tooltip, Divider, Alert, LinearProgress,
  FormControlLabel, Switch, Badge
} from '@mui/material';
import {
  Notifications, NotificationsOff, MarkEmailRead, Warning, Error as ErrorIcon,
  Info, ArrowForward, CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { notificationsApi } from '../../services/api';
import type { Notification, NotificationSeverity } from '../../types';

function SeverityIcon({ severity }: { severity: NotificationSeverity }) {
  if (severity === 'critical') return <ErrorIcon color="error" />;
  if (severity === 'warning') return <Warning color="warning" />;
  return <Info color="info" />;
}

function SeverityColor(severity: NotificationSeverity): string {
  if (severity === 'critical') return 'error.light';
  if (severity === 'warning') return 'warning.light';
  return 'info.light';
}

export default function NotificationList() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsApi.list({ unread_only: unreadOnly, page, limit: LIMIT });
      if (page === 1) {
        setNotifications(res.data.notifications);
      } else {
        setNotifications(prev => [...prev, ...res.data.notifications]);
      }
      setUnreadCount(res.data.unread_count);
      setTotal(res.data.pagination.total);
    } catch (err: any) {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [unreadOnly, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [unreadOnly]);

  const handleMarkRead = async (id: string) => {
    try {
      await notificationsApi.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch {}
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h5">Notifications</Typography>
            {unreadCount > 0 && <Chip label={`${unreadCount} unread`} color="error" size="small" />}
          </Box>
          <Typography variant="body2" color="text.secondary">{total} total notifications</Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <FormControlLabel
            control={<Switch checked={unreadOnly} onChange={e => setUnreadOnly(e.target.checked)} size="small" />}
            label="Unread only"
          />
          {unreadCount > 0 && (
            <Button startIcon={<MarkEmailRead />} onClick={handleMarkAllRead} variant="outlined" size="small">
              Mark all read
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && page === 1 && <LinearProgress sx={{ mb: 2 }} />}

      {notifications.length === 0 && !loading ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <NotificationsOff sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No notifications</Typography>
            <Typography variant="body2" color="text.disabled">
              {unreadOnly ? 'No unread notifications' : 'You\'re all caught up!'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <List disablePadding>
            {notifications.map((notification, i) => (
              <React.Fragment key={notification.id}>
                {i > 0 && <Divider />}
                <ListItem
                  sx={{
                    bgcolor: notification.is_read ? 'transparent' : `${SeverityColor(notification.severity)}22`,
                    transition: 'background 0.2s',
                    '&:hover': { bgcolor: 'action.hover' },
                    pr: 1
                  }}
                  secondaryAction={
                    <Box display="flex" gap={0.5}>
                      {!notification.is_read && (
                        <Tooltip title="Mark as read">
                          <IconButton size="small" onClick={() => handleMarkRead(notification.id)}>
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {notification.contract_id && (
                        <Tooltip title="View contract">
                          <IconButton size="small" onClick={() => navigate(`/contracts/${notification.contract_id}`)}>
                            <ArrowForward fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: SeverityColor(notification.severity), width: 36, height: 36 }}>
                      <SeverityIcon severity={notification.severity} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight={notification.is_read ? 400 : 600}>
                          {notification.title}
                        </Typography>
                        {!notification.is_read && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">{notification.message}</Typography>
                        <Box display="flex" gap={1} mt={0.5} alignItems="center">
                          <Typography variant="caption" color="text.disabled">
                            {formatDistanceToNow(parseISO(notification.created_at), { addSuffix: true })}
                          </Typography>
                          {notification.contract_name && (
                            <Chip label={notification.contract_name} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                          )}
                          <Chip
                            label={notification.severity}
                            size="small"
                            color={notification.severity === 'critical' ? 'error' : notification.severity === 'warning' ? 'warning' : 'info'}
                            sx={{ height: 18, fontSize: 10, textTransform: 'capitalize' }}
                          />
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>

          {notifications.length < total && (
            <Box p={2} textAlign="center">
              <Button onClick={() => setPage(p => p + 1)} disabled={loading} variant="outlined" size="small">
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </Card>
      )}
    </Box>
  );
}
